import React, { useState, Component } from 'react';
import { ArrowLeft, Users, Calendar, Clock, MapPin, Check, Search, Share2, ChevronRight } from 'lucide-react';
import { STORES } from '../../data/mockData';
import { Store } from '../../types';
import { Button } from '../ui/Button';
import { useToast } from '../ToastContext';
interface HostGatheringScreenProps {
  onBack: () => void;
  onNavigate: (screen: any, data?: any) => void;
}
type Step = 'details' | 'venue' | 'confirm';
export function HostGatheringScreen({
  onBack,
  onNavigate
}: HostGatheringScreenProps) {
  const [step, setStep] = useState<Step>('details');
  const [peopleCount, setPeopleCount] = useState<number>(4);
  const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [time, setTime] = useState<string>('18:00');
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const {
    showToast
  } = useToast();
  const gatheringSpots = STORES.filter(s => s.hasGatheringSpot);
  const filteredSpots = gatheringSpots.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()) || s.address.toLowerCase().includes(searchQuery.toLowerCase()));
  const handleShare = () => {
    const message = `I'm hosting a gathering at ${selectedStore?.name}! 🥳\n\n📅 Date: ${date}\n⏰ Time: ${time}\n👥 Spot for: ${peopleCount} people\n\nJoin me here: https://kraya.app/g/host-123`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };
  return <div className="flex flex-col h-full bg-white animate-in fade-in duration-500" data-id="element-1193">
      {/* Header */}
      <div className="px-5 pt-6 pb-4 flex items-center gap-4 border-b border-gray-100" data-id="element-1194">
        <button onClick={() => {
        if (step === 'venue') setStep('details');else if (step === 'confirm') setStep('venue');else onBack();
      }} className="w-10 h-10 bg-gray-50 rounded-full flex items-center justify-center active:scale-95 transition-transform" data-id="element-1195">
          <ArrowLeft size={20} className="text-black" data-id="element-1196" />
        </button>
        <div data-id="element-1197">
          <h1 className="text-xl font-bold text-gray-900" data-id="element-1198">Host a Gathering</h1>
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider" data-id="element-1199">
            {step === 'details' ? 'Step 1: Details' : step === 'venue' ? 'Step 2: Pick a Spot' : 'Step 3: Confirm'}
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-6" data-id="element-1200">
        {step === 'details' && <div className="space-y-8 animate-in slide-in-from-right duration-300" data-id="element-1201">
            <div className="bg-purple-50 rounded-2xl p-6 border border-purple-100" data-id="element-1202">
              <div className="flex items-center gap-3 mb-4" data-id="element-1203">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center" data-id="element-1204">
                  <Users size={20} className="text-purple-600" data-id="element-1205" />
                </div>
                <h2 className="font-bold text-gray-900" data-id="element-1206">How many people?</h2>
              </div>
              <div className="flex items-center justify-between gap-4" data-id="element-1207">
                {[2, 4, 6, 8].map(count => <button key={count} onClick={() => setPeopleCount(count)} className={`flex-1 py-3 rounded-xl font-bold transition-all ${peopleCount === count ? 'bg-purple-600 text-white shadow-lg shadow-purple-200 scale-105' : 'bg-white text-gray-600 border border-purple-100'}`} data-id="element-1208">
                    {count}
                  </button>)}
              </div>
              <p className="text-xs text-purple-600 mt-4 font-medium text-center" data-id="element-1209">
                Gathering spots are optimized for small groups (max 8)
              </p>
            </div>

            <div className="space-y-4" data-id="element-1210">
              <div className="flex items-center gap-3 mb-2" data-id="element-1211">
                <div className="w-10 h-10 bg-gray-50 rounded-full flex items-center justify-center" data-id="element-1212">
                  <Calendar size={20} className="text-gray-600" data-id="element-1213" />
                </div>
                <h2 className="font-bold text-gray-900" data-id="element-1214">When?</h2>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-1215">
                <div className="space-y-1.5" data-id="element-1216">
                  <label className="text-xs font-bold text-gray-400 uppercase ml-1" data-id="element-1217">
                    Date
                  </label>
                  <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full p-4 bg-gray-50 rounded-2xl border-none focus:ring-2 focus:ring-purple-500 font-medium" data-id="element-1218" />
                </div>
                <div className="space-y-1.5" data-id="element-1219">
                  <label className="text-xs font-bold text-gray-400 uppercase ml-1" data-id="element-1220">
                    Time
                  </label>
                  <input type="time" value={time} onChange={e => setTime(e.target.value)} className="w-full p-4 bg-gray-50 rounded-2xl border-none focus:ring-2 focus:ring-purple-500 font-medium" data-id="element-1221" />
                </div>
              </div>
            </div>

            <Button className="w-full py-4 rounded-2xl text-lg shadow-xl mt-8" onClick={() => setStep('venue')} data-id="element-1222">
              Find Available Spots
            </Button>
          </div>}

        {step === 'venue' && <div className="space-y-6 animate-in slide-in-from-right duration-300" data-id="element-1223">
            <div className="relative" data-id="element-1224">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-1225" />
              <input type="text" placeholder="Search by name or area..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-11 pr-4 py-3.5 bg-gray-50 rounded-2xl border-none focus:ring-2 focus:ring-purple-500" data-id="element-1226" />
            </div>

            <div className="space-y-3" data-id="element-1227">
              <h3 className="text-sm font-bold text-gray-400 uppercase ml-1" data-id="element-1228">
                Verified Gathering Spots
              </h3>
              {filteredSpots.length > 0 ? filteredSpots.map(store => <button key={store.id} onClick={() => {
            setSelectedStore(store);
            setStep('confirm');
          }} className="w-full p-4 bg-white border border-gray-100 rounded-2xl flex items-center justify-between hover:border-purple-200 hover:bg-purple-50/30 transition-all active:scale-[0.98] text-left group" data-id="element-1229">
                    <div className="flex-1 min-w-0" data-id="element-1230">
                      <h4 className="font-bold text-gray-900 group-hover:text-purple-700 transition-colors" data-id="element-1231">
                        {store.name}
                      </h4>
                      <p className="text-sm text-gray-500 truncate flex items-center gap-1 mt-0.5" data-id="element-1232">
                        <MapPin size={12} data-id="element-1233" /> {store.address.split(',')[0]}
                      </p>
                      <div className="flex items-center gap-3 mt-2" data-id="element-1234">
                        <span className="text-[10px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full uppercase" data-id="element-1235">
                          Available
                        </span>
                        <span className="text-[10px] font-bold text-gray-400 uppercase" data-id="element-1236">
                          Max {store.gatheringCapacity || 6} people
                        </span>
                      </div>
                    </div>
                    <ChevronRight size={20} className="text-gray-300 group-hover:text-purple-400 transition-colors" data-id="element-1237" />
                  </button>) : <div className="text-center py-12 bg-gray-50 rounded-3xl" data-id="element-1238">
                  <Search size={32} className="mx-auto text-gray-300 mb-2" data-id="element-1239" />
                  <p className="text-gray-500 text-sm" data-id="element-1240">
                    No spots found matching your search
                  </p>
                </div>}
            </div>
          </div>}

        {step === 'confirm' && selectedStore && <div className="space-y-6 animate-in zoom-in duration-300" data-id="element-1241">
            <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-3xl p-8 text-white shadow-2xl relative overflow-hidden" data-id="element-1242">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl" data-id="element-1243" />
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-black/10 rounded-full -ml-12 -mb-12 blur-xl" data-id="element-1244" />

              <div className="relative z-10 text-center" data-id="element-1245">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-inner" data-id="element-1246">
                  <Check size={32} className="text-white" strokeWidth={3} data-id="element-1247" />
                </div>
                <h2 className="text-2xl font-bold mb-1" data-id="element-1248">Spot Reserved!</h2>
                <p className="text-purple-100 text-sm opacity-80" data-id="element-1249">
                  Venue confirmation complete
                </p>
              </div>

              <div className="mt-8 space-y-4 relative z-10" data-id="element-1250">
                <div className="flex items-center justify-between bg-white/10 backdrop-blur-sm p-3 rounded-xl" data-id="element-1251">
                  <span className="text-xs font-medium text-purple-100" data-id="element-1252">
                    Where
                  </span>
                  <span className="text-sm font-bold" data-id="element-1253">
                    {selectedStore.name}
                  </span>
                </div>
                <div className="flex items-center justify-between bg-white/10 backdrop-blur-sm p-3 rounded-xl" data-id="element-1254">
                  <span className="text-xs font-medium text-purple-100" data-id="element-1255">
                    When
                  </span>
                  <span className="text-sm font-bold" data-id="element-1256">
                    {new Date(date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                })}{' '}
                    @ {time}
                  </span>
                </div>
                <div className="flex items-center justify-between bg-white/10 backdrop-blur-sm p-3 rounded-xl" data-id="element-1257">
                  <span className="text-xs font-medium text-purple-100" data-id="element-1258">
                    Group
                  </span>
                  <span className="text-sm font-bold" data-id="element-1259">
                    {peopleCount} People
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-3" data-id="element-1260">
              <h3 className="text-sm font-bold text-gray-900 ml-1" data-id="element-1261">
                Next Steps
              </h3>
              <button onClick={handleShare} className="w-full p-4 bg-[#25D366] text-white rounded-2xl font-bold flex items-center justify-center gap-3 shadow-lg active:scale-95 transition-transform" data-id="element-1262">
                <Share2 size={20} data-id="element-1263" />
                Send Invites on WhatsApp
              </button>
              <button onClick={() => {
            showToast('Contacts synced! Adding friends...', 'info');
          }} className="w-full p-4 bg-black text-[#FFFC00] rounded-2xl font-bold flex items-center justify-center gap-3 shadow-lg active:scale-95 transition-transform" data-id="element-1264">
                <Users size={20} data-id="element-1265" />
                Add People from Kraya
              </button>
            </div>

            <button onClick={() => onNavigate('home')} className="w-full py-4 text-gray-500 font-bold text-sm hover:text-gray-900 transition-colors" data-id="element-1266">
              Back to Home
            </button>
          </div>}
      </div>
    </div>;
}